/**
 * The published wording, read before there is anybody to scope it to —
 * `GET /v1/privacy/consent-texts` (backend `#910`, `#523`).
 *
 * # Why this is not `consents.ts`
 *
 * They serve the same wording and answer different questions, and the
 * difference is who is asking.
 *
 * `GET /v1/privacy/consents` carries **this customer's answers**, so it requires
 * a customer. A person on the sign-up path does not have one — the registration
 * is what creates it — and that route answers them `401`. `consents.ts` says so
 * in its own header and points here.
 *
 * So the sequence had no valid ordering until `go-wallet#910`: read the wording
 * and get a 401, or register first and get `400 consent_required` with no
 * wording, version or hash in the body to retry with. This route is the fix, and
 * it is the ONLY one a sign-up screen may read.
 *
 * # Nothing about anybody crosses it, and that shapes the type
 *
 * `ConsentText` carries no `granted`, no `answered`, no `needsAnswer` — not
 * even as `false`. The backend deliberately omits them rather than zeroing them,
 * because on a route with no caller a `granted: false` reads as "this person
 * refused" and there is nobody for it to be about. Mirroring that here means a
 * screen cannot accidentally render somebody's standing from a document that
 * carries none.
 *
 * # `required` is the server's answer, and must not be re-derived here
 *
 * It is true for exactly the purposes `POST /v1/registrations` will refuse the
 * registration for omitting, and the backend derives it through the same
 * function that handler calls. It is NOT the same predicate as `!choice`: a
 * disclosure published without `required` is shown and not demanded, and a
 * client that inferred "required = not a choice" would send an acceptance for a
 * choice and be refused.
 *
 * Re-deriving it in a browser bundle is the `#25` failure with a legal record
 * behind it — two definitions that agree until the day they do not.
 *
 * # Every field the contract marks required must arrive, or the read fails
 *
 * Same rule as `consents.ts`, for the same reason and one more: these values
 * become the Art. 7(1) evidence that a named person accepted a named version of
 * a named text. There is no field here with a safe default in both directions,
 * so a missing one is an unreadable item rather than a guessed one.
 *
 * `body` is rendered verbatim. It is the text that was hashed, and nothing here
 * trims, wraps, summarises or translates it. **No wording is composed anywhere
 * in this app** — the backend hashes the bytes counsel signed off, so invented
 * text does not fill a gap, it produces a recorded acceptance of words nobody
 * approved.
 */

import { ApiError } from '@/lib/api'
import type { WalletApi } from '@/lib/api'

export const CONSENT_TEXTS_PATH = '/v1/privacy/consent-texts'

export interface ConsentText {
  /** What the processing is, e.g. `terms_of_service`. */
  readonly purpose: string
  /** The one line to render beside the text. */
  readonly summary: string
  /** The full wording, exactly as it must be shown. Never edited on the way here. */
  readonly body: string
  /** Send back as `accepted[].version`. */
  readonly version: number
  /** The hash of `body`. Send back as `accepted[].text_sha256`. */
  readonly textSha256: string
  /**
   * False for a DISCLOSURE — processing on a basis the person's consent has no
   * bearing on. An acknowledgement, never a toggle.
   */
  readonly choice: boolean
  /** Not narrowed to the four the contract lists today — see `decodeConsentText`. */
  readonly lawfulBasis: string
  /**
   * True for exactly the purposes registration refuses the sign-up for
   * omitting. The SERVER'S answer. Never inferred from `choice`.
   */
  readonly required: boolean
}

/**
 * What the sign-up screen knows about the wording.
 *
 * `nothing_published` is separate from `ready` with an empty list for the reason
 * `#124` made expensive on the settings screen: a deployment that has published
 * no wording asks for nothing and records nothing, which is correct behaviour
 * and a sentence somebody can act on. Rendered as an empty list it is a blank
 * panel, which reads as a bug.
 *
 * `not_offered` is separate again. The contract publishes 200, 426 and 500 for
 * this route and no 404, so a 404 cannot be something the handler decided — it
 * can only mean the route was never registered, which is a real deployment
 * state and one no number of retries changes.
 */
export type ConsentTextsState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready'
      readonly locale: string
      readonly texts: readonly ConsentText[]
    }
  /** Nothing is published here. NOT an error, and not an empty list. */
  | { readonly status: 'nothing_published'; readonly locale: string }
  /**
   * This deployment does not serve the route. Not retryable.
   *
   * `detail` is for a log or a test and is NEVER rendered — see the note on
   * `unavailable.reason` for why the two are separated.
   */
  | { readonly status: 'not_offered'; readonly detail?: string }
  /**
   * We could not find out. NOT the same as nothing being asked.
   *
   * `reason` IS SHOWN, so it is a sentence this module wrote about a failure it
   * understands — never a message forwarded from somewhere else. That
   * distinction is `#141`: the reason used to be whatever the failure carried,
   * and a 401 from the customer edge carries "This identity is not linked to a
   * wallet." Rendered under "We could not read the terms" it reads as the
   * explanation, it is not one, and it is alarming on the one screen whose
   * entire job is to link that identity to a wallet.
   *
   * `detail` keeps the original text for a log or a test, where an unfamiliar
   * sentence is information rather than a claim.
   */
  | { readonly status: 'unavailable'; readonly reason: string; readonly detail?: string }

interface ConsentTextDTO {
  purpose?: unknown
  summary?: unknown
  body?: unknown
  version?: unknown
  text_sha256?: unknown
  choice?: unknown
  lawful_basis?: unknown
  required?: unknown
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined
}

/**
 * Decode one text, or say why not.
 *
 * The reason is a sentence rather than a code because it is going to be shown:
 * the screen's honest answer to "what am I agreeing to" is "this app could not
 * read it", and that is more useful with the field named.
 */
export function decodeConsentText(dto: ConsentTextDTO): ConsentText | string {
  const purpose = str(dto.purpose)
  if (purpose === undefined) return 'a consent text arrived with no purpose'

  // Blank is the same failure as missing. A disclosure with no text is not a
  // disclosure, and an empty panel under a heading tells somebody they were
  // shown something they were not.
  const body = str(dto.body)
  if (body === undefined) return `${purpose} arrived with no wording`

  const textSha256 = str(dto.text_sha256)
  if (textSha256 === undefined) return `${purpose} arrived with no content hash`

  const summary = str(dto.summary)
  if (summary === undefined) return `${purpose} arrived with no summary`

  const lawfulBasis = str(dto.lawful_basis)
  if (lawfulBasis === undefined) return `${purpose} arrived with no lawful basis`

  if (
    typeof dto.version !== 'number' ||
    !Number.isInteger(dto.version) ||
    dto.version < 1
  ) {
    return `${purpose} arrived with no usable version (${String(dto.version)})`
  }

  for (const [key, value] of [
    ['choice', dto.choice],
    ['required', dto.required],
  ] as const) {
    if (typeof value !== 'boolean') {
      return `${purpose} arrived without ${key}, and there is no safe value to assume for it`
    }
  }

  return {
    purpose,
    summary,
    body,
    version: dto.version,
    textSha256,
    // NOT narrowed to the four bases the contract lists today — the same call
    // `consents.ts` makes. Nothing branches on the basis: `choice` decides
    // whether a control may exist and `required` decides whether it is
    // demanded, and both are checked above. So an unknown basis costs a pretty
    // label, and refusing on it would cost somebody the wording they came to
    // read on the day the backend adds a fifth.
    lawfulBasis,
    choice: dto.choice as boolean,
    required: dto.required as boolean,
  }
}

/**
 * Read the wording a registration will be checked against.
 *
 * NO SESSION IS REQUIRED and none is assumed. The caller is on the sign-up path
 * by definition — that is the only place this is read — so this must never be
 * gated on being a customer, which is the whole of `go-wallet#910`.
 */
export async function fetchConsentTexts(
  api: WalletApi,
  opts: { readonly signal?: AbortSignal } = {},
): Promise<ConsentTextsState> {
  let dto: { locale?: unknown; texts?: unknown }
  try {
    dto = await api.get<{ locale?: unknown; texts?: unknown }>(CONSENT_TEXTS_PATH, {
      ...opts,
      // The contract declares this route `auth: none`, so a 401 from it is not
      // the session layer's business. Without this the sign-up screen fires a
      // fresh Ory round trip every time the read fails, for a person whose
      // session is perfectly good.
      sessionless: 'the contract declares this route auth: none (go-wallet#910)',
    })
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err)

    // A 404, 401 or 403 all mean ONE thing here, and it is the same thing: the
    // route was never registered and something else answered in its place. The
    // contract publishes 200, 426 and 500 for this path and nothing else — and
    // it needs no session, so a status about sessions cannot have come from it.
    //
    // Measured rather than reasoned: on staging the route fell through to the
    // customer edge and answered 401 `customer_session.unauthenticated`, which
    // is what `go-wallet#986` fixes. Treating that as transient offered a retry
    // that could never succeed, which is the trap the 404 branch was already
    // written to avoid.
    if (err instanceof ApiError && [404, 401, 403].includes(err.status)) {
      return { status: 'not_offered', detail }
    }
    return {
      status: 'unavailable',
      reason: 'we could not reach the terms just now',
      detail,
    }
  }

  if (!Array.isArray(dto?.texts)) {
    // OUR sentence about a shape we can see, so this one is shown as-is.
    return { status: 'unavailable', reason: 'the terms arrived in a form this app could not read' }
  }

  const locale = str(dto.locale) ?? ''

  const texts: ConsentText[] = []
  for (const raw of dto.texts as ConsentTextDTO[]) {
    const decoded = decodeConsentText(raw ?? {})
    // One unreadable item fails the whole read rather than silently shortening
    // the list. A person shown three of the four things they are agreeing to
    // has no way to know, and the hidden one is as likely as any to be the one
    // that matters — and here it would also be one the registration then
    // demands and is refused for.
    if (typeof decoded === 'string') return { status: 'unavailable', reason: decoded }
    texts.push(decoded)
  }

  return texts.length === 0
    ? { status: 'nothing_published', locale }
    : { status: 'ready', locale, texts }
}

/** The purposes registration will refuse the sign-up for omitting. */
export function requiredTexts(state: ConsentTextsState): readonly ConsentText[] {
  return state.status === 'ready' ? state.texts.filter((t) => t.required) : []
}

/**
 * May a registration be submitted from this state?
 *
 * `false` for every state in which we cannot say what was displayed —
 * INCLUDING the ones that are not errors. `not_offered` and `unavailable` are
 * the sharp cases: the registration would very likely succeed, because a
 * deployment that does not serve the wording has published none and demands
 * none. Submitting anyway would create a customer whose file records that
 * nothing was asked of them, on a day we could not tell whether anything was —
 * which is the false statement `#97` and `#101` are about, with a supervisory
 * authority as the reader.
 *
 * `nothing_published` DOES allow it, and that is not the same thing: there we
 * read the deployment's answer and it said nothing is asked.
 */
export function mayRegister(
  state: ConsentTextsState,
  accepted: ReadonlySet<string>,
): boolean {
  if (state.status === 'nothing_published') return true
  if (state.status !== 'ready') return false
  return requiredTexts(state).every((t) => accepted.has(t.purpose))
}
